import axios, { CancelTokenSource } from 'axios'
import * as Constants from '@/services/Constants'
import { ApiGetRoomResponse, ApiRoomDocument, ApiRoomRequest } from '../RoomContext'
import { env } from './EnvironmentVariables'
import { logger } from './Logger'
import { getAuthTokenFromLocalUserCache } from '@/Logic'

const log = logger.createChildLogger({ name: 'Api' })

let _authToken = ''
let _apiBasePath = ''

let httpsAgent: any = undefined

function init(apiBasePath: string, authToken: string) {
    _authToken = authToken
    _apiBasePath = apiBasePath
}

export type FileMetadata = {
    filePath: string
    filename: string
    filesize: number
    mimetype: string
}

let getUserSelfPromise: Promise<any> | null = null

async function getUserSelf() {
    try {
        const headers = {
            Authorization: 'Bearer ' + _authToken,
            'X-Client-Version': Constants.appversion,
        }

        if (!getUserSelfPromise) {
            getUserSelfPromise = axios.get(`${_apiBasePath}/v1/users/_me`, {
                headers,
                httpsAgent,
            })
        }
        const response = await getUserSelfPromise
        const user = response.data
        getUserSelfPromise = null
        return user
    } catch (e) {
        getUserSelfPromise = null
        if ((e as any).response) {
            throw (e as any).response
        } else {
            throw new Error('No response received')
        }
    }
}

function createUploadedFile(
    roomUrn: string,
    roomType: string,
    file: File,
    fileMetadata: FileMetadata,
    onProgress: (progressEvent: any) => void,
    onEnd: (result: { uuid: string } | null, error: any | null) => void
): CancelTokenSource {
    const headers = {
        Authorization: 'Bearer ' + _authToken,
        'X-Client-Version': Constants.appversion,
    }
    const cancel = axios.CancelToken.source()

    const logctx = {
        roomUrn,
        filename: fileMetadata.filename,
        filesize: fileMetadata.filesize,
    }

    const tryUpload = async (options?: { s3Provider?: string }) => {
        if (env.TEST_CHAT_FILE_INITIAL_UPLOAD_ERROR && !options?.s3Provider) {
            throw new Error('Test')
        }

        // Get upload URL with optional s3Provider
        const metadata = {
            ...fileMetadata,
            s3Provider: options?.s3Provider || undefined,
        }

        const {
            data: { fileUuid, uploadUrl },
        } = await axios.post(`${_apiBasePath}/v1/rooms/${roomUrn}/uploadedfiles?roomType=${roomType}`, metadata, {
            headers,
            httpsAgent,
        })

        // Rename file to match fileUuid
        const renamedFile = new File([file], fileUuid, {
            type: file.type,
            lastModified: file.lastModified,
        })
        log.verbose('chatFileUpload', `got fileUuid="${fileUuid}" uploadUrl="${uploadUrl}".`, logctx)

        // Upload to storage
        await axios.put(uploadUrl, renamedFile, {
            cancelToken: cancel.token,
            onUploadProgress: (progressEvent) => {
                const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total)
                log.verbose('chatFileUpload', `progress=${percentCompleted}.`, logctx)
                onProgress(progressEvent)
            },
            httpsAgent,
        })

        return fileUuid
    } // end tryUpload

    ;(async () => {
        log.checkpoint('file_upload_start', '', logctx)

        try {
            let fileUuid = ''
            try {
                fileUuid = await tryUpload()
            } catch (firstError) {
                if (firstError instanceof axios.Cancel) {
                    log.checkpoint('file_upload_canceled', '', logctx)
                    onEnd(null, { code: 'canceled' })
                    return
                }

                // retry upload
                log.checkpoint('file_upload_failed_initial', '', logctx)
                fileUuid = await tryUpload({ s3Provider: 'cloudflare' })
            }

            log.checkpoint('file_upload_success', '', { ...logctx, fileUuid })
            onEnd({ uuid: fileUuid }, null)
        } catch (e: any) {
            if (e instanceof axios.Cancel) {
                log.checkpoint('file_upload_canceled', '', logctx)
                onEnd(null, { code: 'canceled' })
            } else if (e.response) {
                log.error('file_upload_failed_final', '', { response: e.response })
                onEnd(null, e.response.data)
            } else if (e.request) {
                log.error('file_upload_failed_final', '', { code: 'network' })
                onEnd(null, { code: 'network', e })
            } else {
                log.error('file_upload_failed_final', '', { e })
                onEnd(null, { code: 'exception', e })
            }
        }
    })()

    return cancel
}

async function removeUploadedFiles(roomUrn: string, roomType: string, fileUuids: string[]) {
    const headers = {
        Authorization: 'Bearer ' + _authToken,
        'X-Client-Version': Constants.appversion,
    }

    try {
        // Get S3 upload token url (the "uploadUrl") from the server.
        const response = await axios.delete(
            `${_apiBasePath}/v1/rooms/${roomUrn}/uploadedfiles/_multi?roomType=${roomType}`,
            {
                headers: headers,
                data: {
                    fileUuids: fileUuids,
                },
                httpsAgent: httpsAgent,
            }
        )
    } catch (e) {}
}

async function connectCalendar() {
    try {
        const headers = {
            Authorization: 'Bearer ' + _authToken,
            'X-Client-Version': Constants.appversion,
        }

        // get OAuth login URL
        const response = await axios.get(`${_apiBasePath}/v1/users/_me/oauth-login-url?selector=google`, {
            headers: headers,
            httpsAgent: httpsAgent,
        })
        return response.data
    } catch (e) {
        console.error(e)
        return null
    }
}

async function getRoom(roomUrn: string, password?: string) {
    try {
        const headers = {
            Authorization: 'Bearer ' + _authToken,
            'X-Client-Version': Constants.appversion,
        }

        const response = await axios.get<ApiGetRoomResponse>(`${_apiBasePath}/v1/rooms/${roomUrn}`, {
            headers: headers,
            httpsAgent: httpsAgent,
            params: {
                ...(password ? { p: password } : {}),
            },
        })

        const room: ApiRoomDocument = response.data?.room
        if (typeof room !== 'object') {
            throw new Error('Incorrect response received')
        }

        return room
    } catch (e) {
        if ((e as any).response) {
            return null
        } else {
            return null
        }
    }
}

async function updateRoom(roomUrn: string, data: Partial<ApiRoomRequest>) {
    try {
        const headers = {
            Authorization: 'Bearer ' + _authToken,
            'X-Client-Version': Constants.appversion,
        }

        const response = await axios.patch<ApiGetRoomResponse>(`${_apiBasePath}/v1/rooms/${roomUrn}`, data, {
            headers: headers,
            httpsAgent: httpsAgent,
        })

        return response.data
    } catch (e) {
        if ((e as any).response) {
            return null
        } else {
            return null
        }
    }
}

async function deleteRoom(roomUrn: string, options?: RequestInit): Promise<void> {
    const opts = buildOptionsWithAuth({ ...options, method: 'DELETE' })
    const res = await fetch(Constants.api_base_path + `/v1/rooms/${roomUrn}`, opts)
    if (!res.ok) {
        throw new Error(`Failed to delete room '${roomUrn}': ${res.status}`)
    }
}

function buildOptionsWithAuth(options?: RequestInit): RequestInit {
    const storedAuthToken = getAuthTokenFromLocalUserCache()

    let headers: Record<string, string> = {}

    if (options?.headers) {
        if (options.headers instanceof Headers) {
            options.headers.forEach((value, key) => {
                headers[key] = value
            })
        } else if (Array.isArray(options.headers)) {
            options.headers.forEach(([key, value]) => {
                headers[key] = value
            })
        } else {
            headers = { ...options.headers }
        }
    }

    if (storedAuthToken) {
        headers['Authorization'] = 'Bearer ' + storedAuthToken
    }

    headers['X-Client-Version'] = env.APP_VERSION

    return {
        ...options,
        headers,
    }
}

const Api = {
    init,
    getUserSelf,
    createUploadedFile,
    removeUploadedFiles,
    connectCalendar,
    getRoom,
    updateRoom,
    deleteRoom,
}

export default Api
